export type ProgramPeriodKey =
  | "this-week"
  | "four-weeks"
  | "this-month"
  | "last-month";

export type ProgramPeriodRange = {
  from: string;
  to: string;
};

export const PROGRAM_PERIOD_OPTIONS: ReadonlyArray<{
  key: ProgramPeriodKey;
  label: string;
}> = [
  { key: "this-week", label: "This week" },
  { key: "four-weeks", label: "4 weeks" },
  { key: "this-month", label: "This month" },
  { key: "last-month", label: "Last month" },
];

export const DEFAULT_PROGRAM_PERIOD: ProgramPeriodKey = "four-weeks";

/** The backend rejects a range longer than this. */
const MAX_RANGE_DAYS = 400;

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/** Monday 00:00 UTC, matching the `weekStart` the weekly series is bucketed on. */
const startOfWeekUtc = (at: number): number => {
  const d = new Date(at);
  const day = (d.getUTCDay() + 6) % 7;
  return Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate() - day,
    0,
    0,
    0,
    0,
  );
};

const startOfMonthUtc = (at: number, offsetMonths = 0): number => {
  const d = new Date(at);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offsetMonths, 1);
};

const bounds = (
  period: ProgramPeriodKey,
  now: number,
): { from: number; to: number } => {
  switch (period) {
    case "this-week": {
      const from = startOfWeekUtc(now);
      return { from, to: from + WEEK_MS };
    }
    case "four-weeks": {
      const to = startOfWeekUtc(now) + WEEK_MS;
      return { from: to - 4 * WEEK_MS, to };
    }
    case "this-month":
      return { from: startOfMonthUtc(now), to: startOfMonthUtc(now, 1) };
    case "last-month":
      return { from: startOfMonthUtc(now, -1), to: startOfMonthUtc(now) };
  }
};

/**
 * Half-open `[from, to)` so a study falls in exactly one period, and clamped to
 * the backend's 400-day cap: an over-long range is rejected outright rather
 * than truncated server-side, which would fail the whole dashboard.
 */
export const getProgramPeriodRange = (
  period: ProgramPeriodKey,
  now: number,
): ProgramPeriodRange => {
  const { from, to } = bounds(period, now);
  const clampedFrom = Math.max(from, to - MAX_RANGE_DAYS * DAY_MS);
  return {
    from: new Date(clampedFrom).toISOString(),
    to: new Date(to).toISOString(),
  };
};

export const getProgramPeriodLabel = (period: ProgramPeriodKey): string =>
  PROGRAM_PERIOD_OPTIONS.find(({ key }) => key === period)?.label ?? period;
